/**
 * Contracts of the Visit reschedule dialog: what the commands that open it pass and get back, the form and
 * preview state it holds, and the pure mappings the dialog uses to build appointments and copy.
 */
import { visitAppointmentPassed, VisitBrowseRow } from '@jms/model';
import { VisitDateOverride } from '@jms/model/entities/planned.work/visit/visit.dates';
import { DateTime } from 'luxon';
import { VisitBookingChoice, VisitDatesResult, VisitMovePreview, VisitMovePreviewRequest, VisitMoveTarget } from '../../visit.movement.types';

/** Whether the person picks a date or joins an upcoming Visit of the line. */
export enum VisitRescheduleDestination {
  Date = 'Date',
  Visit = 'Visit',
}

/** What the person chose; the caller turns it into the Reschedule request. */
export interface VisitRescheduleChoice {
  /** The picked appointment, or the joined Visit's own when `targetVisitId` is set. */
  dates: VisitDateOverride;
  /** The upcoming Visit joined (its browse `_id`, forecast ids included); absent for a picked date. */
  targetVisitId?: string;
  /** Present only for a booked Visit. */
  booking?: VisitBookingChoice;
  allowOutsideWindow: boolean;
  /** The preview the person reviewed; absent when the appointment is unchanged. */
  fingerprint?: string;
  /** "Keep approved" was offered and left on: the caller runs the move as Reschedule with `keepApproved`. */
  keepApproved?: boolean;
}

/**
 * Runs the move from inside the dialog so a stale review can be shown without closing it. Undefined when the
 * run already reported its own failure; the dialog then stays open without a second error.
 */
export type VisitRescheduleSubmit = (choice: VisitRescheduleChoice) => Promise<VisitDatesResult | undefined>;

/** The dialog's answer: the choice, plus the server's result when the dialog ran the move itself. */
export interface VisitRescheduleOutcome {
  choice: VisitRescheduleChoice;
  result?: VisitDatesResult;
}

/** How the dialog opens: a passed appointment's decision, whether it offers Keep approved, and who runs the move. */
export interface VisitDatesDialogOptions {
  decision?: boolean;
  /** The Visit holds a person's approval the move may keep; the dialog then shows "Keep approved", on. */
  approvable?: boolean;
  /** Runs the move inside the dialog, so a stale review reopens on the refreshed preview. */
  submit?: VisitRescheduleSubmit;
}

/** The dates dialog's form. `targetVisitId` is the upcoming Visit picked under "Join an upcoming visit". */
export interface VisitAppointmentModel {
  destination: VisitRescheduleDestination;
  date: Date;
  startTime: Date;
  endTime: Date;
  allDay: boolean;
  /** The joined Visit's browse `_id`; read only while `destination` is Visit. */
  targetVisitId?: string | null;
  booking?: VisitBookingChoice;
  allowOutsideWindow: boolean;
  /** Present only when the dialog offers Keep approved. */
  keepApproved?: boolean;
}

/** Hint under "Keep approved", shared by the record and bulk reschedule dialogs. */
export const KEEP_APPROVED_HINT = 'Moved tasks stay approved. Turn off to move them as unapproved.';

/** A preview request with the local date it moves to, which the panel names. */
export interface VisitMovePreviewAsk {
  request: VisitMovePreviewRequest;
  /** Local YYYY-MM-DD in the Visit's zone. */
  date: string;
}

/** One preview fetch, keyed by the request it answers so submit can tell whether it is current. */
export interface VisitMovePreviewState {
  key: string | null;
  /** Local YYYY-MM-DD the previewed move lands on. */
  date?: string;
  loading: boolean;
  preview?: VisitMovePreview;
  error?: string;
  /** Set when submit found the reviewed preview stale; cleared by the next change. */
  stale: boolean;
}

/** "Confirm date" or "Reschedule this visit". */
export function visitDatesSubmitLabel(decision: boolean): string {

  if (decision) return 'Confirm date';
  return 'Reschedule this visit';
}

/** The dates section's title, before the zone. */
export function visitDatesDialogTitle(decision: boolean): string {

  if (decision) return 'Confirm appointment';
  return 'Reschedule visit';
}

/** Only a passed appointment can land on a past date, so only then do the past-date rules apply. */
export function visitDatesDescription(row: VisitBrowseRow, decision: boolean, now: Date, pastDateRules: string): string | null {

  if (decision) return `This visit's appointment has passed. Keep the date to confirm it happened then, or pick a new one. ${pastDateRules}`;
  if (visitAppointmentPassed(row, now)) return pastDateRules;
  return null;
}

/** What the preview panel says before there is a preview to show. */
export function visitMovePreviewHint(decision: boolean, joining: boolean): string {

  if (decision) return 'Keeping this date confirms the visit happened then.';
  if (joining) return 'Pick a visit to join to see what changes.';
  return 'Pick a new date or time to see what changes.';
}

/** The "Visit to join" field's description: why there is nothing to pick yet, or nothing at all. */
export function visitMoveTargetsDescription(loading: boolean, failed: boolean, count: number): string {

  if (loading) return 'Finding upcoming visits…';
  if (failed) return 'Upcoming visits could not be loaded. You can still pick a date.';
  if (!count) return 'No upcoming visit of this line falls inside the compliance window.';
  return '';
}

/** Builds the appointment from the form; an overnight attendance finishes the following day. */
export function appointmentFromModel(row: VisitBrowseRow, model: VisitAppointmentModel): VisitDateOverride {

  const date = DateTime.fromJSDate(new Date(model.date), { zone: row.timezone }).startOf('day');
  if (!date.isValid) throw new Error('Choose a valid date');
  let start = date;
  let end = date.plus({ days: 1 });
  if (!model.allDay) {
    const startTime = DateTime.fromJSDate(new Date(model.startTime), { zone: row.timezone });
    const endTime = DateTime.fromJSDate(new Date(model.endTime), { zone: row.timezone });
    if (!startTime.isValid || !endTime.isValid) throw new Error('Choose valid start and end times');
    start = date.set({ hour: startTime.hour, minute: startTime.minute });
    end = date.set({ hour: endTime.hour, minute: endTime.minute });
    if (end < start) end = end.plus({ days: 1 });
  }
  if (!start.isValid || !end.isValid || end <= start) throw new Error('End time must be after the start time');

  return { startDate: start.toJSDate(), endDate: end.toJSDate(), allDay: model.allDay };
}

/** A target's appointment as a dates request: what joining it adopts. */
export function targetAppointment(target: VisitMoveTarget): VisitDateOverride {

  return { startDate: new Date(target.startDate), endDate: new Date(target.endDate), allDay: target.allDay };
}

/** True when `dates` is the row's current appointment: a confirmation, not a move. */
export function sameAppointment(row: VisitBrowseRow, dates: VisitDateOverride): boolean {

  return dates.allDay === (row.allDay === true)
    && dates.startDate.getTime() === new Date(row.startDate).getTime()
    && dates.endDate.getTime() === new Date(row.endDate).getTime();
}

/** Equal requests share a key, so submit reuses the preview the person is looking at. */
export function visitMovePreviewKey(request: VisitMovePreviewRequest): string {

  return JSON.stringify(request);
}

export function visitMoveDayLabel(row: VisitBrowseRow, dates: VisitDateOverride): string {

  return DateTime.fromJSDate(dates.startDate, { zone: row.timezone }).toFormat('d LLL');
}
